/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { DialogInput, NotificationService, WaitingService } from '@gh/core-ui';
import { GridColumn, GridLayout, GridSettingsDialogConfig } from '@gh/core-ui-grid';

import { difference, sortBy } from 'lodash';
import { GridSettingsService, Preset, PRICES_FILED, DATES_FILED, DETAILS_FILED, TABLE_HAS_PRESETS, PRESETS_IDS } from '../../services';
import { DictionaryService, FormState, FormStateBuilder, IS_REGION_US } from '@gh/common-shared';
import { combineLatest$ } from '@gh/rx';
import { TranslationService } from '@gh/core-mls';

export interface CheckboxItem<T> {
  value: T;
  checked: boolean;
}

@Component({
  selector: 'gh-grid-settings-dialog',
  template: require('./grid-settings-dialog.component.html'),
  styles: [require('./grid-settings-dialog.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridSettingsDialogComponent<T> implements DialogInput<GridSettingsDialogConfig>, OnInit {
  form: FormState<{ preset: Preset }>;
  presets: Preset[];
  currentPreset: Preset | '';
  input: GridSettingsDialogConfig;
  title: string;
  entity: T;
  hasCategories: boolean;
  isOpen = {
    basic: {
      visible: true,
      nonVisible: true,
    },
    dates: {
      visible: false,
      nonVisible: false,
    },
    prices: {
      visible: false,
      nonVisible: false,
    },
    details: {
      visible: false,
      nonVisible: false,
    },
  };

  nonVisibleFields: CheckboxItem<GridColumn>[];
  visibleFields: CheckboxItem<GridColumn>[];

  nonVisibleBasic: CheckboxItem<GridColumn>[];
  visibleBasic: CheckboxItem<GridColumn>[];

  nonVisibleDates: CheckboxItem<GridColumn>[];
  visibleDates: CheckboxItem<GridColumn>[];

  nonVisiblePrices: CheckboxItem<GridColumn>[];
  visiblePrices: CheckboxItem<GridColumn>[];

  nonVisibleDetails: CheckboxItem<GridColumn>[];
  visibleDetails: CheckboxItem<GridColumn>[];

  get hasVisibleChecked(): boolean {
    return !!this.visibleFields && !!this.visibleFields.find((item: CheckboxItem<GridColumn>) => item.checked);
  }

  get hasNonVisibleChecked(): boolean {
    return !!this.nonVisibleFields && !!this.nonVisibleFields.find((item: CheckboxItem<GridColumn>) => item.checked);
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private notificationService: NotificationService,
    public dialogRef: MatDialogRef<GridSettingsDialogConfig>,
    private waitingService: WaitingService,
    private formStateBuilder: FormStateBuilder,
    private translations: TranslationService,
    private gridService: GridSettingsService,
    private dictionaryService: DictionaryService,
  ) {
    this.hasCategories = this.gridService.pagHasCategories();
    const currentTable =  this.gridService.getCurrentTable();
    if (currentTable && TABLE_HAS_PRESETS[currentTable]) {
      this.presets = [];
    }
  }

  ngOnInit(): void {
    this.form = this.formStateBuilder.buildPlain({
      children: {
        preset: {
          required: false,
          validators: [],
        },
      },
    });
    const currentTable =  this.gridService.getCurrentTable();
    if (currentTable && TABLE_HAS_PRESETS[currentTable]) {
      combineLatest$([this.dictionaryService.get('PRESETS'), this.gridService.getPresetsAsync()])
        .subscribe(([dictionary, presets]) => {
          const columns = [...this.input.layout.columns];
          if (!presets.find((item) => item.name === 'PERSONAL')) {
            this.gridService.setPersonalPreset(columns.filter((column) => column.visible)
              .map((column) => column.column));
            this.changeDetectorRef.detectChanges();
          }
          let resultPresets = this.gridService.getPresets().map((preset) => ({
            ...preset,
            label: dictionary.get(preset.name),
          }));
          if (this.isMasterView()) {
            const corporateFilterIds = ['CORPORATE_DUE', 'CORPORATE_PAID'];
            resultPresets = resultPresets.filter((item) => !corporateFilterIds.includes(item.name));
          }
          if (IS_REGION_US) {
            const auNzFilterIds = ['CORPORATE_DUE', 'CORPORATE_PAID'];
            resultPresets = resultPresets.filter((item) => !auNzFilterIds.includes(item.name));
          }
          resultPresets = resultPresets.filter((item) => PRESETS_IDS[currentTable].includes(item.name));

          this.presets = resultPresets;
          this.currentPreset = this.gridService.getCurrentPreset();
          if (this.currentPreset) {
            this.form.group.controls['preset'].setValue(this.currentPreset.name);
          }
          this.changeDetectorRef.detectChanges();
        });

    }
    this.setInitial();
  }

  onSubmit(): any {
    const visibleColumns = this.getVisibleColumns();
    const nonVisibleColumns = this.getNonVisibleColumns().map((item: CheckboxItem<GridColumn>) => ({
      ...item.value,
      visible: false,
      position: 1,
    }));
    const layout = {
      ...this.input.layout,
      columns: [...visibleColumns, ...nonVisibleColumns],
    };
    if (!visibleColumns.length) {
      return this.notificationService.error('msg_table_visible_elements_is_empty');
    }
    this.gridService.setCurrentPreset(this.currentPreset);
    return this.input.onSave(layout).pipe(
      this.waitingService.operator())
      .subscribe((gridLayout: GridLayout) => {
        this.dialogRef.close(gridLayout);
        if (this.currentPreset && this.currentPreset.name === 'PERSONAL') {
          this.gridService.setPersonalPreset(layout.columns.filter((item) => item.visible)
            .map((item) => item.column));
        }
        this.notificationService.success('msg_saved_successfully');
      }, this.notificationService.processAll); // tslint:disable-line:align
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  moveToVisible(): void {
    const visibleColumns = this.getVisibleColumns().map((item) => ({value: item, checked: false }));
    const {from, to } = this.moveChecked(this.getNonVisibleColumns(), visibleColumns);
    this.nonVisibleFields = from;
    this.visibleFields = to;
    this.setFieldsGroup();
  }

  moveToNonVisible(): void {
    const visibleColumns = [
      ...this.visibleBasic,
      ...this.visiblePrices,
      ...this.visibleDates,
      ...this.visibleDetails,
    ];

    const {from, to } = this.moveChecked(visibleColumns, this.getNonVisibleColumns());
    this.visibleFields = from;
    this.nonVisibleFields = to;
    this.setFieldsGroup();
  }

  moveChecked(from: CheckboxItem<GridColumn>[], to: CheckboxItem<GridColumn>[]): any {
    const checked = from.filter((item: CheckboxItem<GridColumn>) => item.checked);
    from = difference(from, checked);
    for (let i = 0; i < checked.length; i++) { // tslint:disable-line:prefer-for-of
      checked[i].checked = false;
      to.push((checked[i]));
    }
    return {from, to};
  }

  dropField(event: CdkDragDrop<GridColumn[]> | any): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex);
      this.nonVisibleFields = [ ...this.nonVisibleBasic, ...this.nonVisibleDates, ...this.nonVisiblePrices, ...this.nonVisibleDetails];
      this.visibleFields = [ ...this.visibleBasic, ...this.visibleDates, ...this.visiblePrices, ...this.visibleDetails];
    }
  }

  setFieldsGroup(): void {
    if (this.hasCategories) {
      this.nonVisibleBasic = this.nonVisibleFields.filter((column) => !DATES_FILED.includes(column.value.column)
        && !PRICES_FILED.includes(column.value.column) && !DETAILS_FILED.includes(column.value.column));
      this.nonVisibleDates = this.nonVisibleFields.filter((column) => DATES_FILED.includes(column.value.column));
      this.nonVisiblePrices = this.nonVisibleFields.filter((column) => PRICES_FILED.includes(column.value.column));
      this.nonVisibleDetails = this.nonVisibleFields.filter((column) => DETAILS_FILED.includes(column.value.column));

      this.visibleBasic = this.visibleFields.filter((column) => !DATES_FILED.includes(column.value.column)
        && !PRICES_FILED.includes(column.value.column) && !DETAILS_FILED.includes(column.value.column));
      this.visibleDates = this.visibleFields.filter((column) => DATES_FILED.includes(column.value.column));
      this.visiblePrices = this.visibleFields.filter((column) => PRICES_FILED.includes(column.value.column));
      this.visibleDetails = this.visibleFields.filter((column) => DETAILS_FILED.includes(column.value.column));
    } else {
      this.nonVisibleBasic = [...this.nonVisibleFields];
      this.nonVisibleDates = [];
      this.nonVisiblePrices = [];
      this.nonVisibleDetails = [];

      this.visibleBasic = [...this.visibleFields];
      this.visibleDates = [];
      this.visiblePrices = [];
      this.visibleDetails = [];
    }

    this.changeDetectorRef.detectChanges();
  }

  setPositions(list: CheckboxItem<GridColumn>[], startIndex: number): GridColumn[] {
    return list.map((item: CheckboxItem<GridColumn>, i: number) => ({
      ...item.value,
      visible: true,
      position: i + startIndex,
    }));
  }

  translate(name: string): string {
    return this.input.translate(name);
  }

  toggleGroup(groupName: string, visibleType: string): void {
    this.isOpen[groupName][visibleType] = !this.isOpen[groupName][visibleType];
    this.isOpen[groupName].nonVisible = this.isOpen[groupName][visibleType];
    this.isOpen[groupName].visible = this.isOpen[groupName][visibleType];
    if (this.isOpen[groupName][visibleType]) {
      for (const key in this.isOpen) {
        if (this.isOpen.hasOwnProperty(key) && key !== groupName) {
          this.isOpen[key].nonVisible = false;
          this.isOpen[key].visible = false;
        }
      }
    }
  }

  getNonVisibleColumns(): CheckboxItem<GridColumn>[] {
    return  [
      ...this.nonVisibleBasic,
      ...this.nonVisiblePrices,
      ...this.nonVisibleDates,
      ...this.nonVisibleDetails,
    ];
  }

  getVisibleColumns(): GridColumn[] {
    let lastIndex = 1;
    const visibleBasic = this.setPositions(this.visibleBasic, lastIndex);
    lastIndex = visibleBasic.length ? visibleBasic[visibleBasic.length - 1].position : lastIndex;
    const visibleDates = this.setPositions(this.visibleDates, lastIndex);
    lastIndex = visibleDates.length ? visibleDates[visibleDates.length - 1].position : lastIndex;
    const visiblePrices = this.setPositions(this.visiblePrices, lastIndex);
    lastIndex = visiblePrices.length ? visiblePrices[visiblePrices.length - 1].position : lastIndex;
    const visibleDetails = this.setPositions(this.visibleDetails, lastIndex);
    return  [
      ...visibleBasic,
      ...visibleDates,
      ...visiblePrices,
      ...visibleDetails,
    ];
  }

  onSelectPreset(presetName: string): void {
    this.currentPreset = this.presets.find((item) => item.name === presetName) || '';
    const columns = sortBy([...this.input.layout.columns].map((column) => ({
      value: {...column},
      checked: false,
    })), 'position'); // tslint:disable-line:align
    if (this.currentPreset && this.currentPreset.columns) {
      // @ts-ignore
      this.nonVisibleFields = columns.filter((field) => (!this.currentPreset.columns.includes(field.value.column)));
      // @ts-ignore
      this.visibleFields = columns.filter((field) => (this.currentPreset.columns.includes(field.value.column)));
      this.setFieldsGroup();
    }
  }
  private isMasterView(): boolean {
    const view = window.sessionStorage.getItem('view');
    return view ? view.toLowerCase() === 'master' : false;
  }

  private setInitial(): void {
    const columns = [...this.input.layout.columns];
    this.nonVisibleFields = sortBy(columns.filter((column) => !column.visible).map((column) => ({
      value: {...column},
      checked: false,
    })), 'position'); // tslint:disable-line:align

    this.visibleFields = sortBy(columns.filter((column) => column.visible).map((column) => ({
      value: {...column},
      checked: false,
    })), 'position'); // tslint:disable-line:align
    this.setFieldsGroup();
  }
}
